import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Icon,
  useColorMode,
  useColorModeValue,
  Tooltip,
  Avatar,
  Tab,
  Tabs,
  TabList,
  useToast,
} from '@chakra-ui/react';
import {
  SunIcon,
  MoonIcon,
  SettingsIcon,
} from '@chakra-ui/icons';
import { FaSignOutAlt, FaHome, FaCog } from 'react-icons/fa';
import NewCompassLogo from '../common/NewCompassLogo';
import { useAuth } from '../../contexts/AuthContext';
import { ThemePanel } from './ThemePanel';
import { useAppInfoContext } from '../../contexts/AppInfoContext';

interface HeaderProps {
  // No GitLab browser props needed anymore
}

export const Header: React.FC<HeaderProps> = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { user, logout } = useAuth();
  const { appInfo } = useAppInfoContext();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');

  const handleLogout = () => {
    logout();
  };

  const handleTabChange = (index: number) => {
    if (index === 0) {
      navigate('/');
    } else if (index === 1) {
      navigate('/settings');
    }
  };

  const getCurrentTabIndex = () => {
    if (location.pathname === '/settings') return 1;
    return 0; // default to Home
  };

  // Determine if Settings tab should be visible:
  // Only show for admins AND not on shared routes
  const isSharedRoute = location.pathname.startsWith('/shared/');
  const showSettingsTab = user?.isAdmin === true && !isSharedRoute;


  return (
    <Box
      bg={bg}
      borderBottom="1px"
      borderColor={borderColor}
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex="1000"
      h="80px"
    >
      <Flex
        h="80px"
        alignItems="center"
        justifyContent="space-between"
        px={6}
      >
        {/* Left side - Logo and Navigation Tabs */}
        <HStack spacing={6}>
          {/* Logo */}
          <HStack spacing={2}>
            <NewCompassLogo boxSize={8} />
            <Text
              fontSize="xl"
              fontWeight="bold"
              color={textColor}
              display={{ base: 'none', md: 'block' }}
            >
              {appInfo.name}
            </Text>
          </HStack>

          {/* Navigation Tabs */}
          <Tabs
            index={getCurrentTabIndex()}
            onChange={handleTabChange}
            variant="line"
            colorScheme="primary"
          >
            <TabList border="none">
              <Tab
                _selected={{
                  color: useColorModeValue('blue.500', 'blue.300'),
                  borderColor: useColorModeValue('blue.500', 'blue.300'),
                }}
                fontWeight="medium"
              >
                <HStack spacing={2}>
                  <Icon as={FaHome} />
                  <Text>Home</Text>
                </HStack>
              </Tab>
              {showSettingsTab && (
                <Tab
                  _selected={{
                    color: useColorModeValue('blue.500', 'blue.300'),
                    borderColor: useColorModeValue('blue.500', 'blue.300'),
                  }}
                  fontWeight="medium"
                >
                  <HStack spacing={2}>
                    <Icon as={FaCog} />
                    <Text>Settings</Text>
                  </HStack>
                </Tab>
              )}
            </TabList>
          </Tabs>
        </HStack>

        {/* Right side - Actions */}
        <HStack spacing={2}>
          {/* Theme toggle */}
          <Tooltip
            label={colorMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            placement="bottom"
          >
            <IconButton
              aria-label="Toggle color mode"
              icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
              onClick={toggleColorMode}
              variant="ghost"
              size="lg"
            />
          </Tooltip>

          {/* Theme Panel */}
          <ThemePanel />

          {/* User menu */}
          <Menu>
            <MenuButton>
              <Tooltip label={`Logged in as ${user?.username}`} placement="bottom">
                <Avatar
                  size="sm"
                  name={user?.username}
                  bg="primary.500"
                  color="white"
                  cursor="pointer"
                />
              </Tooltip>
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={handleLogout}
                icon={<Icon as={FaSignOutAlt} />}
                color="red.500"
              >
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  );
};